import React, { useContext, useRef } from 'react'

import { Link } from '@driftt/dds'

import ActivityContentToolTip from 'components/ActivityContentTooltip/index'
import { AccountActivityAccountAvatar } from 'components/Avatars'
import EngagementScore from 'components/EngagementScore'
import { AccountDrawerContext } from 'context/accountDrawerContext'
import usePathUtils from 'hooks/usePathUtils'
import { ActivityGroupByAccountAndEndUsersBody, ActivityType, Insight } from 'modules/accountActivity/domain'
import { ActivityEndUser, CompanyData } from 'modules/accountActivity/domain'
import { pluralize } from 'utils/formatStrings'
import { timeSinceUpdated } from 'utils/timeStamp'
import { segmentTrack, TrackType } from 'utils/tracking'

import styles from './styles.module.css'

interface CardHeaderProps {
  insight: Insight
  visitorList?: ActivityEndUser[]
  title?: React.ReactElement
}

const CardHeader: React.FC<CardHeaderProps> = ({ insight, visitorList = [], title }) => {
  const { isAccountDetailsPage } = usePathUtils()
  const { setIsOpen: setIsDrawerOpen, setAccountId: setSelectedAccountId } = useContext(AccountDrawerContext)
  const accountDisplayName =
    insight.accountData?.name ||
    insight.companyData?.name ||
    insight.companyData?.domain ||
    'Unnamed account'

  const ref = useRef<HTMLDivElement>(null)

  const body = insight.body as ActivityGroupByAccountAndEndUsersBody
  const hasWebsiteActivity = body.activityGroupsByEndUser?.some(userActivity => 
    userActivity.activityList.some(activityItem => {
      switch(activityItem.activityType){
        case ActivityType.CONTRACT_REQUEST:
        case ActivityType.CONVERSATION:
        case ActivityType.END_USER_IDENTIFIED:
        case ActivityType.FILLED_OUT_FORM:
        case ActivityType.MEETING_BOOKED:
        case ActivityType.MEETING_CANCELED:
        case ActivityType.MEETING_RECORDED:
        case ActivityType.MEETING_RESCHEDULED:
        case ActivityType.OTHER_CHATBOT_CONVERSATION:
        case ActivityType.PAGE_VIEW:
        case ActivityType.UNSUBSCRIBED:
        case ActivityType.VIDEO:
        case ActivityType.WEBINAR:
          return true
        default:
          return false
      }
    }))

  const createCompanyLinkWithName = (accountId: string, companyData?: CompanyData) => (
    <>
      {companyData ? (
        <ActivityContentToolTip inCrm={!!insight.accountData} data={companyData}>
          <Link 
            variant='object'
            onClick={() => {
              setIsDrawerOpen(true)
              setSelectedAccountId(accountId)
            }}>{accountDisplayName}</Link>
        </ActivityContentToolTip>
      ) : (
        <Link 
          variant='object'
          onClick={() => {
            setIsDrawerOpen(true)
            setSelectedAccountId(accountId)
            segmentTrack(TrackType.INSIGHT_FEED_VIEW_ACCOUNT, {
            insightId: insight.id,
            accountId: insight.accountId})
          }}>{accountDisplayName}</Link>
      )}
    </>
  )

  return (
    <div className={styles.headerContainer} ref={ref} id="headerContainer">
      <div className={styles.avatarAndContact}>
        {insight.accountData ? (
          <Link 
          variant='object'
          onClick={() => {
            setIsDrawerOpen(true)
            setSelectedAccountId(insight.accountId)
            segmentTrack(TrackType.INSIGHT_FEED_VIEW_ACCOUNT, {
            insightId: insight.id,
            accountId: insight.accountId})
          }}>
            <AccountActivityAccountAvatar
              imageUrl={insight.companyData?.logo}
              inCrm={!!insight.accountData}
            />
          </Link>
        ) : (
          <AccountActivityAccountAvatar
            imageUrl={insight.companyData?.logo}
            inCrm={!!insight.accountData}
          />
        )}
        <div className={styles.headerText}>
          {title ?? (
            <div className={styles.firstContactAndCompany}>
              {createCompanyLinkWithName(insight.accountId, insight.companyData)}
              {visitorList.length > 0 && (
                <div className={styles.users}>
                  {' '}
                  {`${pluralize('user', visitorList.length)}`}{' '}
                  {visitorList.length > 1 ? 'were' : 'was'} active 
                  {hasWebsiteActivity ? ' on your site.' : '.'}
                </div>
              )}
            </div>
          )}
          <div className={styles.cardSubheader}>
            <span className={styles.cardSubheaderText}>{`${timeSinceUpdated(
              insight.lastActivityAt
            )} ago`}</span>
          </div>
        </div>
      </div>
      {insight.scoredAccount && !isAccountDetailsPage && (
        <div className={styles.engagementScore}>
          <EngagementScore boundaryRef={ref.current} accountActivity={insight.scoredAccount} />
        </div>
      )}
    </div>
  )
}

export default CardHeader
